import {Component, Input} from '@angular/core';
import {BaseChart} from "../base-chart.component";
import {ChartConfiguration, Plugin, ScaleOptions} from "chart.js";
import {ChartTypeRegistry, ScaleOptionsByType} from "chart.js/dist/types";

@Component({
    selector: 'app-scatter-chart',
    templateUrl: './scatter-chart.component.html',
    styleUrls: ['./scatter-chart.component.scss']
})
export class ScatterChartComponent extends BaseChart<'scatter'> {
    @Input() height = 500;
    @Input() axisMode: 'indexed' | 'linear' = 'indexed';
    @Input() xAxisTitle: string;
    @Input() yAxisTitle: string;
    @Input() showLegend = false;
    @Input() useEnergyProfileOptions = false;
    @Input() xLabels: string[];
    @Input() yLabels: string[];
    @Input() lineAxes: ScatterData;
    @Input() visibleBuildingTypes: string[];
    @Input() scales: {
        [key: string]: ScaleOptionsByType<ChartTypeRegistry['scatter']['scales']>;
    } = {};
    @Input() plugins: Plugin<'scatter'>[] = [];
    @Input() suggestedMinY: number;
    @Input() pluginOptions: any = {};
    @Input() gridLineColor: string;
    @Input() layoutPadding: number | {top?: number; right?: number; bottom?: number; left?: number} = {bottom: 30};

    getOptions(): ChartConfiguration<'scatter'>['options'] {
        const scales: any = this.getDefaultScales();
        Object.entries(this.scales).forEach(([key, value]) => {
            const update = value || {};
            scales[key] = {...(scales[key] || {}), ...update};
        });
        const options: ChartConfiguration<'scatter'>['options'] = {
            ...(this.useEnergyProfileOptions ? {} : {
                parsing: false,
                normalized: true
            }),
            maintainAspectRatio: false,
            scales: scales,
            layout: {
                padding: this.layoutPadding
            },
            animation: {
                duration: 0
            },
            plugins: {
                datalabels: {
                    display: false
                },
                legend: {
                    display: this.showLegend
                },
                title: {
                    display: !!this.chartTitle,
                    text: this.chartTitle,
                    font: {
                        size: 16
                    }
                },
                decimation: {
                    enabled: !this.useEnergyProfileOptions,
                    ...(this.useEnergyProfileOptions ? {} : {
                        algorithm: 'lttb',
                        samples: 500
                    })
                }
            }
        };
        Object.entries(this.pluginOptions).forEach(([key, value]) => {
            const current = (options.plugins[key] || {}) as Record<string, any>;
            const incoming = (value || {}) as Record<string, any>;
            options.plugins[key] = {
                ...current,
                ...incoming
            };
        });
        return options;
    };

    private getDefaultScales = (): {
        [key: string]: ScaleOptions;
    } => this.axisMode === 'linear'
        ? ({
            x: {
                type: 'linear',
                title: {
                    display: !!this.xAxisTitle,
                    text: this.xAxisTitle
                },
                grid: {
                    display: false
                },
                offset: false
            },
            y: {
                type: 'linear',
                title: {
                    display: !!this.yAxisTitle,
                    text: this.yAxisTitle
                },
                grid: {
                    display: true,
                    color: this.gridLineColor,
                    lineWidth: 1,
                },
                suggestedMin: this.suggestedMinY,
                beginAtZero: false
            }
        })
        : ({
            x: {
                labels: this.xLabels,
                suggestedMin: 0,
                suggestedMax: this.xLabels.length - 1,
                grid: {
                    display: false
                },
                offset: true,
                ticks: {
                    stepSize: 1,
                    autoSkip: false,
                    source: "labels",
                    color: "white",
                    callback: (label, index, labels) => {
                        return this.xLabels[label];
                    }
                }
            },
            y: {
                display: true,
                suggestedMin: this.suggestedMinY,
                suggestedMax: (this.yLabels.length - 1) * 100,
                border: {
                    dash: [0]
                },
                grid: {
                    display: true,
                    color: this.gridLineColor,
                    lineWidth: 1,
                },
                ticks: {
                    autoSkip: false,
                    source: "labels",
                    color: "white",
                    callback: (label, index, labels) => {
                        const l: number = label as number;
                        if (l < 0) {
                            return "";
                        }
                        return this.yLabels[l / 100];
                    }
                },
                beginAtZero: false
            }
        });
}

export interface ScatterData {
    data: ScatterRecord[];
}

interface ScatterRecord {
    x: number;
    y: number;
    label: string;
}
